import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { appConstants } from "utils/appConstants";
import { savePdfResponse } from "utils/documentPdf";
import { rctiTimestamp } from "utils/rctiTimestamp";
import { useMaterialUIController } from "context";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import RCTIDocument from "./RCTIDocument";
import "layouts/Invoices/invoiceFlow.css";
import "./rctiDetail.css";
import "./rctiDocumentJourney.css";
import "./rctiIssuedDetail.css";
import "./supplierRcti.css";
import "./rctiResponsive.css";
export default function SupplierRCTI() {
  const { id } = useParams(),
    location = useLocation();
  const [controller] = useMaterialUIController();
  const token = location.hash.slice(1);
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [pdfError, setPdfError] = useState("");
  const [records, setRecords] = useState([]),
    [selection, setSelection] = useState(null),
    [registerError, setRegisterError] = useState(""),
    [registerPage, setRegisterPage] = useState(1),
    [hasMore, setHasMore] = useState(false),
    [registerBusy, setRegisterBusy] = useState(false);
  const request = useCallback(
    async (action, fields = {}) => {
      const response = await axios.post(
        `${appConstants.baseURL}rcti-received/${id}/${action}`,
        { token, ...fields },
        { timeout: 60000 }
      );
      if (!response.data?.Result_Status)
        throw new Error(response.data?.Result_Message || "RCTI unavailable.");
      return response.data.Result_Output;
    },
    [id, token]
  );
  const loadRecords = useCallback(
    async (page = 1) => {
      setRegisterBusy(true);
      setRegisterError("");
      try {
        const result = await request("records", { page });
        setRecords((current) =>
          page === 1
            ? result.records || []
            : [...current, ...(result.records || [])]
        );
        setRegisterPage(page);
        setHasMore(Boolean(result.hasMore));
      } catch {
        setRegisterError(
          "Your received RCTI list could not be loaded. Please retry."
        );
      } finally {
        setRegisterBusy(false);
      }
    },
    [request]
  );
  const viewRecord = async (row) => {
    if (busy) return;
    setBusy(true);
    setPdfError("");
    const selected = { documentID: row.documentID, version: row.version };
    try {
      const result = await request("view", selected);
      setData(result);
      setSelection(selected);
    } catch {
      setPdfError(
        "This issued version could not be opened. Your current document is still available."
      );
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
  useEffect(() => {
    let active = true;
    setData(null);
    setRecords([]);
    setSelection(null);
    setError("");
    setPdfError("");
    if (!/^[a-f0-9]{64}$/.test(token)) {
      setError(
        "This private link is incomplete. Open the complete link from your RCTI email."
      );
      return;
    }
    request("view")
      .then((result) => {
        if (active) {
          setData(result);
          loadRecords();
        }
      })
      .catch(() => {
        if (active)
          setError(
            "This private RCTI link is unavailable or has expired. Ask the buyer to resend the RCTI."
          );
      });
    return () => {
      active = false;
    };
  }, [request, token, loadRecords]);
  const download = async () => {
    if (busy) return;
    setBusy(true);
    setPdfError("");
    try {
      const result = await request("pdf", selection || {});
      savePdfResponse({ data: result.pdf }, result.filename);
    } catch {
      setPdfError("The PDF could not be downloaded. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const record = data?.document;
  const currency = /^[A-Z]{3}$/.test(
    record?.currencyCode || record?.currencyValue || ""
  )
    ? record.currencyCode || record.currencyValue
    : "AUD";
  const money = (value) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency }).format(
      Number(value || 0)
    );
  const date = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-AU", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "Australia/Sydney",
        })
      : "Not recorded";
  const total = Number(record?.finalAmount || 0),
    history = data?.history || [],
    originalTotal = history[0]?.total ?? total;
  const adjusted = history.length > 0,
    delta = (Math.round(total * 100) - Math.round(originalTotal * 100)) / 100;
  return (
    <div
      className={`invoice-flow-shell invoice-detail-shell rcti-detail-shell rcti-supplier-shell${
        controller.darkMode ? " dark" : ""
      }`}
    >
      <main className="rcti-detail-page rcti-issued-journey rcti-supplier-content">
        {!data && !error && (
          <PageSkeleton
            type="detail"
            variant="document"
            label="Opening your secure RCTI…"
          />
        )}
        {error && (
          <section className="invoice-flow-card" role="alert">
            <h1>RCTI unavailable</h1>
            <p>{error}</p>
          </section>
        )}
        {data && (
          <>
            <header className="rcti-detail-header">
              <h1>{record.rctiNumber}</h1>
              <p>
                {record.buyerIdentity?.name || record.businessName} · Issued{" "}
                {date(record.issuedAt || record.rctiDate)} · Read-only supplier
                copy
              </p>
            </header>
            <div className="rcti-detail-context">
              <div>
                <strong>
                  {adjusted
                    ? "Adjusted RCTI · original preserved"
                    : "Recipient Created Tax Invoice"}
                </strong>
                <span>
                  This private view contains the issued document. Contact the
                  buyer with questions. Your link is valid until{" "}
                  {date(data.expiresAt)}.
                </span>
              </div>
            </div>
            <div className="rcti-issued-toolbar">
              <div>
                <span className="rcti-status status-draft">
                  {record.lifecycleStatus === "invalidated"
                    ? "Archived · Replaced"
                    : "Issued · Locked"}
                </span>
                {adjusted && (
                  <span className="rcti-status status-draft">Adjusted</span>
                )}
              </div>
              <button
                className="rcti-detail-action"
                disabled={busy}
                onClick={download}
              >
                {busy ? "Please wait…" : "Download RCTI"}
              </button>
            </div>
            {pdfError && <p role="alert">{pdfError}</p>}
            <section
              className="invoice-flow-card supplier-rcti-register"
              aria-label="Received RCTIs and adjustments"
            >
              <h2>Your RCTIs &amp; adjustments</h2>
              <p>
                Issued documents from{" "}
                {record.buyerIdentity?.name || record.businessName}. Select a
                document or a preserved version to view it below.
              </p>
              {registerError && (
                <p role="alert">
                  {registerError}{" "}
                  <button
                    className="rcti-detail-action"
                    disabled={registerBusy}
                    onClick={() => loadRecords(1)}
                  >
                    Retry list
                  </button>
                </p>
              )}
              {records.length > 0 ? (
                <div className="supplier-rcti-register-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>RCTI</th>
                        <th>Version</th>
                        <th>Issued</th>
                        <th>Total</th>
                        <th>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((row) => (
                        <tr key={`${row.documentID}-${row.version}`}>
                          <td>{row.number}</td>
                          <td>
                            {row.version > 1
                              ? `Adjustment · v${row.version}`
                              : `Original · v${row.version}`}
                            {row.historical ? " · Historical" : ""}
                          </td>
                          <td>{date(row.issuedAt)}</td>
                          <td>
                            {new Intl.NumberFormat("en-AU", {
                              style: "currency",
                              currency: /^[A-Z]{3}$/.test(row.currency)
                                ? row.currency
                                : "AUD",
                            }).format(row.total)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="rcti-detail-action"
                              disabled={busy}
                              aria-label={`View ${row.number} version ${row.version}`}
                              onClick={() => viewRecord(row)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>
                  {registerBusy
                    ? "Loading issued documents…"
                    : registerError
                    ? ""
                    : "No other issued documents found."}
                </p>
              )}
              {hasMore && (
                <button
                  className="rcti-detail-action"
                  disabled={registerBusy}
                  onClick={() => loadRecords(registerPage + 1)}
                >
                  {registerBusy ? "Loading…" : "Load more RCTIs"}
                </button>
              )}
            </section>
            <div className="rcti-detail-layout">
              <div className="rcti-issued-document-column">
                <RCTIDocument
                  issuedDetail
                  values={{ ...record, versions: history }}
                  agreement={
                    data.agreement
                      ? {
                          reference: data.agreement.reference,
                          version: data.agreement.wordingVersion,
                        }
                      : null
                  }
                  items={data.items}
                  documents={[]}
                  showAttachments={false}
                  organisation={
                    record.buyerIdentity?.name || record.businessName || "Buyer"
                  }
                  loadingPage={false}
                  currencyCode={currency}
                  subtotalExcludingGst={
                    total - Number(record.totalGSTAmount || 0)
                  }
                  gstSummaryLabel="GST (included)"
                  total={total}
                  money={money}
                  dateValue={date}
                />
                {adjusted && (
                  <section className="invoice-flow-card rcti-detail-state-card">
                    <h2>Issue &amp; adjustment history</h2>
                    <ol className="rcti-issued-history">
                      {history.map((row) => (
                        <li key={row.version}>
                          <time>{rctiTimestamp(row.adjustedAt)}</time>
                          <p>
                            Version {row.version} preserved · {money(row.total)}
                          </p>
                          <p>{row.reason}</p>
                        </li>
                      ))}
                      <li>
                        Issued version {record.version} · {money(total)}
                      </li>
                    </ol>
                  </section>
                )}
              </div>
              <aside className="rcti-detail-aside">
                <section className="invoice-flow-card rcti-balance-card">
                  <h2>Outstanding payable</h2>
                  {data.payment.isCurrent ? (
                    <>
                      <span className="rcti-type-emphasis">
                        {money(data.payment.outstanding)}
                      </span>
                      <p>
                        {data.payment.status ||
                          (data.payment.paid > 0 &&
                          data.payment.outstanding <= 0
                            ? "Paid"
                            : data.payment.paid > 0
                            ? "Part-paid"
                            : "Unpaid")}
                      </p>
                    </>
                  ) : (
                    <p>
                      Historical issued version. Ask the buyer for the latest
                      payable information.
                    </p>
                  )}
                </section>
                <section className="invoice-flow-card rcti-breakdown-card">
                  <h2>Balance breakdown</h2>
                  <dl className="rcti-c13-balances">
                    <div>
                      <dt>Original RCTI</dt>
                      <dd>{money(originalTotal)}</dd>
                    </div>
                    {adjusted && (
                      <div>
                        <dt>Adjustments</dt>
                        <dd>
                          {delta < 0 ? "−" : "+"}
                          {money(Math.abs(delta))}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt>Issued total</dt>
                      <dd>{money(total)}</dd>
                    </div>
                    {data.payment.isCurrent && (
                      <>
                        <div>
                          <dt>Paid</dt>
                          <dd>{money(data.payment.paid)}</dd>
                        </div>
                        <div className="rcti-c13-outstanding">
                          <dt>Outstanding {currency}</dt>
                          <dd>{money(data.payment.outstanding)}</dd>
                        </div>
                      </>
                    )}
                  </dl>
                  <p>
                    Payable to{" "}
                    {record.supplierIdentity?.name ||
                      record.supplierCompanyName}
                    . No payment is initiated from this page.
                  </p>
                </section>
                {data.agreement && (
                  <section className="invoice-flow-card">
                    <h2>Agreement reference</h2>
                    <p>
                      {data.agreement.reference} · Wording v
                      {data.agreement.wordingVersion}
                    </p>
                    <p>
                      Issued under the accepted RCTI agreement between the
                      parties.
                    </p>
                  </section>
                )}
                <section className="invoice-flow-card">
                  <h2>Questions about this RCTI?</h2>
                  <p>
                    Contact {record.buyerIdentity?.name || record.businessName}.
                  </p>
                  {record.buyerIdentity?.emailAddress && (
                    <a href={`mailto:${record.buyerIdentity.emailAddress}`}>
                      {record.buyerIdentity.emailAddress}
                    </a>
                  )}
                  <p>Retain the issued PDF with your business records.</p>
                </section>
              </aside>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
